import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import {
  Card,
  CardHeader,
  Text,
  Caption1,
  Tooltip,
  Button,
} from '@fluentui/react-components';
import { Shimmer } from '@fluentui/react';
import {
  Warning16Regular,
} from '@fluentui/react-icons';

import { DropdownMenuButton, IDropdownMenuButtonProps } from './components/dropdown-menu-button';
import { DialogEditButton, IDialogEditButtonProps } from './components/dialog-edit-button';
import { StatusSummaryCardIcon, IIconProps } from './components/icon';
import { useStyles } from './styles/styles';

export interface IStatusSummaryCardProps extends IIconProps {
  header: string;
  description: string;
  dropdownMenuProps?: IDropdownMenuButtonProps;
  dialogEditProps?: IDialogEditButtonProps;
  isLoading: boolean;
  className?: string;
  warningMessage?: string;
}

export const StatusSummaryCard: React.FunctionComponent<IStatusSummaryCardProps> = memo(({
  header,
  description,
  IconComponent,
  color,
  dropdownMenuProps,
  dialogEditProps,
  isLoading,
  className,
  warningMessage,
}: IStatusSummaryCardProps) => {
  const classes = useStyles();

  const action = useMemo(() => {
    if (dropdownMenuProps) {
      return (<DropdownMenuButton {...dropdownMenuProps} />);
    }

    if (dialogEditProps) {
      return (<DialogEditButton {...dialogEditProps} />);
    }

    return null;
  }, [dialogEditProps, dropdownMenuProps]);

  return (
    <div className={classNames('ba-status-summary-card', classes.cardContainer, className)}>
      <Card className={classes.card} size="small">
        {isLoading ?
          <CardHeader
            image={<Shimmer width={44} styles={{ shimmerWrapper: { height: '44px' } }} />}
            header={<Shimmer width="100%" />}
            description={<Shimmer width="80%" style={{ marginTop: '5px' }} />}
          /> :
          <CardHeader
            image={
              <StatusSummaryCardIcon
                IconComponent={IconComponent}
                color={color}
              />
            }
            header={
              <Tooltip content={header} relationship="description">
                <Text
                  truncate
                  wrap={false}
                  weight="semibold"
                  className={classNames('ba-status-summary-card-text', classes.cardText)}
                >
                  {header}
                </Text>
              </Tooltip>
            }
            description={
              <Tooltip content={description} relationship="description">
                <Caption1
                  truncate
                  wrap={false}
                  className={classNames('ba-status-summary-card-description', classes.cardDescription)}
                >
                  {description}
                  {warningMessage &&
                  <div className={classNames('ba-status-summary-card-warning', classes.cardWarning)}>
                    <Button
                      size="small"
                      appearance="transparent"
                      icon={<Warning16Regular className={classNames('ba-status-summary-card-icon-warning', classes.iconWarning)} />}
                    />
                    {warningMessage}
                  </div>
                  }
                </Caption1>
              </Tooltip>
            }
            action={action}
          />}
      </Card>
    </div>
  );
});

StatusSummaryCard.defaultProps = {
  dropdownMenuProps: undefined,
  dialogEditProps: undefined,
  className: '',
  warningMessage: undefined,
};
